import type { Resultado } from './acesso';
import type { Sessao } from './auth';
import { escreverNaApi, lerDaApi } from './giro';
import type { Escrita } from './giro';

/**
 * METAS & PREMIAÇÃO — o front de /api/metas (dono, 29/09; fase 2: admin). Tipos espelham
 * @inovapay/domain/metas e @inovapay/database/metas. A TELA NÃO CALCULA dinheiro: régua, prêmio e
 * simulação vêm do servidor (item 39 do dono).
 */

export type Direcao = 'MAIOR_MELHOR' | 'MENOR_MELHOR';
export type RegraDosNiveis =
  | { tipo: 'COMPOSTO'; passosPct: string[] }
  | { tipo: 'SOBRE_BASE'; passosPct: string[] }
  | { tipo: 'VALOR_FIXO'; passo: string; quantidade: number }
  | { tipo: 'MANUAL'; alvos: string[] };
export type RegraDoPremio =
  { tipo: 'NENHUM' } | { tipo: 'POR_NIVEL'; valores: string[] } | { tipo: 'UNICO'; valor: string };
export type BaseDaMeta =
  | { tipo: 'INDIVIDUAL'; meses: 1 | 3 | 6 | 12 }
  | { tipo: 'LOJA'; meses: 1 | 3 | 6 | 12 }
  | { tipo: 'FIXA'; valor: string }
  | { tipo: 'SEM_BASE' };
export interface Requisito {
  indicador: string;
  rotulo: string;
  operador: '>=' | '<=';
  valor: string;
}
export interface RegraDaMeta {
  filtro: { grupos?: string[]; classificacoes?: string[] } | null;
  direcao: Direcao;
  base: BaseDaMeta;
  niveis: RegraDosNiveis;
  nomesDosNiveis?: string[];
  premiacao: {
    ativa: boolean;
    premio: RegraDoPremio;
    acumulo: 'MAIOR_NIVEL' | 'SOMA';
    tetoDaMeta: string | null;
  };
  requisitos: { modo: 'TODOS' | 'ALGUM'; itens: Requisito[] };
  amostraMinima: number;
  toleranciaDeRiscoPct: string;
  publico: { tipo: 'TODOS' | 'CONTAS'; contas: string[]; excecoes: string[] };
  personalizadas: Record<string, { base?: string; alvos?: string[] }>;
  inicio: string;
  fim: string | null;
  peso: number;
}

export type StatusDaMeta = 'RASCUNHO' | 'ATIVA' | 'PAUSADA' | 'ENCERRADA' | 'EXCLUIDA';
export interface MetaGravada {
  readonly id: string;
  readonly nome: string;
  readonly descricao: string | null;
  readonly categoria: string | null;
  readonly icone: string | null;
  readonly indicador: string;
  readonly tipo: 'PERMANENTE' | 'CAMPANHA';
  readonly status: StatusDaMeta;
  readonly ordem: number;
  readonly versao: number;
  readonly regra: RegraDaMeta | null;
  readonly vigenteDesde: string | null;
  readonly agendada: { versao: number; vigenteDesde: string; regra: RegraDaMeta } | null;
  readonly atualizadoEm: string;
  readonly atualizadoPor: string | null;
  /** O máximo que paga neste mês (null = sem premiação), calculado pelo servidor. */
  readonly premioMaximo: string | null;
}
export interface ConfigDasMetas {
  tetoGeral: string | null;
  diaDePagamento: number;
  intervaloDeAlertasHoras: number;
  versao: number;
}
export interface IndicadorDoCatalogo {
  readonly codigo: string;
  readonly rotulo: string;
  readonly categoria: 'VENDAS' | 'MARGEM' | 'DESCONTO' | 'ENTREGA' | 'CATEGORIAS' | 'ESTOQUE';
  readonly unidade: 'RS' | 'PCT' | 'UN' | 'RAZAO';
  readonly casas: number;
  readonly natureza: 'RAZAO' | 'SOMA';
  readonly direcaoSugerida: Direcao;
  readonly amostra: 'cupons' | 'dias' | 'entregas' | 'itens' | 'lancamentos';
  readonly aceitaFiltro: boolean;
  readonly observacao?: string;
}
export interface OpcoesDasMetas {
  readonly grupos: readonly { grupo: string; classificacao: string; produtos: number }[];
  readonly pessoas: readonly { id: string; nome: string; login: string }[];
}
export interface Previa {
  readonly problemas: readonly string[];
  readonly alvos: readonly string[] | null;
  readonly premios: readonly string[];
  readonly premioMaximo: string;
}
export interface AvaliacaoDaMeta {
  readonly situacao: 'SEM_DADO' | 'SEM_BASE' | 'PARCIAL' | 'VALIDA';
  readonly alvos: readonly string[] | null;
  readonly nivel: number;
  readonly nivelMaximo: number;
  readonly comPremio: boolean;
  /** Os campos de dinheiro (e os alvos de indicador em R$) vêm `null` no modo "só números". */
  readonly premio: string | null;
  readonly premioSeValidar: string | null;
  readonly bloqueio: readonly string[] | null;
  readonly proximo: {
    nivel: number;
    alvo: string | null;
    falta: string | null;
    ganho: string | null;
  } | null;
  readonly risco: {
    limite: string | null;
    folga: string | null;
    valorEmRisco: string | null;
  } | null;
}
export interface Simulacao {
  readonly competencia: string;
  readonly porConta: readonly {
    accountId: string;
    nome: string;
    meta: {
      base: string | null;
      valor: string | null;
      amostra: number;
      situacao: string;
      avaliacao: AvaliacaoDaMeta | null;
    } | null;
  }[];
  readonly funcionarios: number;
  readonly comDados: number;
  readonly elegiveis: number;
  readonly total: string;
  readonly media: string;
  readonly maximo: string;
  readonly semHistorico: boolean;
}
export interface VersaoDaMeta {
  readonly versao: number;
  readonly regra: RegraDaMeta;
  readonly vigenteDesde: string;
  readonly motivo: string;
  readonly em: string;
  readonly autor: string | null;
}

export const lerCatalogo = (): Promise<Resultado<IndicadorDoCatalogo[]>> =>
  lerDaApi('/api/metas/catalogo');
export const lerMetas = (): Promise<Resultado<{ metas: MetaGravada[]; config: ConfigDasMetas }>> =>
  lerDaApi('/api/metas');
export const lerOpcoes = (): Promise<Resultado<OpcoesDasMetas>> => lerDaApi('/api/metas/opcoes');
export const lerHistorico = (id: string): Promise<Resultado<VersaoDaMeta[]>> =>
  lerDaApi(`/api/metas/${id}/historico`);

export interface DadosDaMeta {
  id?: string | null;
  versaoEsperada?: number | null;
  nome: string;
  descricao?: string | null;
  categoria?: string | null;
  icone?: string | null;
  indicador: string;
  tipo: 'PERMANENTE' | 'CAMPANHA';
  status?: 'RASCUNHO' | 'ATIVA';
  ordem?: number;
  regra: RegraDaMeta;
  aplicar?: 'PROXIMA' | 'AGORA';
  confirmarAgora?: boolean;
  motivo: string;
}
export const salvarMeta = (
  s: Sessao,
  d: DadosDaMeta,
): Promise<Escrita<{ id: string; versao: number; vigenteDesde: string }>> =>
  escreverNaApi(s, 'POST', '/api/metas', d);
export const mudarStatus = (
  s: Sessao,
  id: string,
  status: 'ATIVA' | 'PAUSADA' | 'EXCLUIDA',
  motivo: string,
): Promise<Escrita<null>> =>
  escreverNaApi(s, 'POST', `/api/metas/${id}/status`, { status, motivo });
export const salvarConfig = (
  s: Sessao,
  config: Omit<ConfigDasMetas, 'versao'>,
  versaoEsperada: number,
  motivo: string,
): Promise<Escrita<{ versao: number }>> =>
  escreverNaApi(s, 'PUT', '/api/metas/config', { config, versaoEsperada, motivo });
export const previaDaRegua = (
  s: Sessao,
  indicador: string,
  regra: RegraDaMeta,
  base: string | null,
): Promise<Escrita<Previa>> =>
  escreverNaApi(s, 'POST', '/api/metas/previa', { indicador, regra, base });
export const simular = (
  s: Sessao,
  d: {
    indicador: string;
    nome?: string;
    regra: RegraDaMeta;
    competencia?: string;
    conta?: string | null;
  },
): Promise<Escrita<Simulacao>> => escreverNaApi(s, 'POST', '/api/metas/simular', d);

/* ------------------------------------------------------------------ *
 * Formatação (só texto — nunca conta)
 * ------------------------------------------------------------------ */

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
/** "10" → "R$ 10,00" (o texto do servidor vira número SÓ para formatar). */
export const reais = (v: string | null | undefined) => (v == null ? '—' : brl.format(Number(v)));
export function valorDoIndicador(
  v: string | null | undefined,
  unidade: IndicadorDoCatalogo['unidade'],
  casas: number,
) {
  if (v == null) return '—';
  const n = Number(v).toLocaleString('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
  if (unidade === 'RS') return `R$ ${n}`;
  if (unidade === 'PCT') return `${n}%`;
  return n;
}
/** Como `valorDoIndicador`, mas sem valor devolve '' (o balconista em "só números" não vê R$). */
export function valorOuVazio(
  v: string | null | undefined,
  unidade: IndicadorDoCatalogo['unidade'],
  casas: number,
): string {
  return v == null ? '' : valorDoIndicador(v, unidade, casas);
}
export const ROTULO_DA_CATEGORIA: Record<IndicadorDoCatalogo['categoria'], string> = {
  VENDAS: 'Vendas',
  MARGEM: 'Margem',
  DESCONTO: 'Desconto',
  ENTREGA: 'Entrega',
  CATEGORIAS: 'Categorias',
  ESTOQUE: 'Estoque',
};
export const ROTULO_DA_BASE = (b: BaseDaMeta): string =>
  b.tipo === 'INDIVIDUAL'
    ? b.meses === 1
      ? 'Mês anterior (individual)'
      : `Média individual ${String(b.meses)} meses`
    : b.tipo === 'LOJA'
      ? `Média da loja ${String(b.meses)} ${b.meses === 1 ? 'mês' : 'meses'}`
      : b.tipo === 'FIXA'
        ? `Valor fixo ${b.valor}`
        : 'Sem base (valores manuais)';
export const nivelNome = (r: RegraDaMeta | null, k: number) =>
  r?.nomesDosNiveis?.[k - 1]?.trim() || `Nível ${String(k)}`;
export const quantosNiveis = (n: RegraDosNiveis) =>
  n.tipo === 'MANUAL'
    ? n.alvos.length
    : n.tipo === 'VALOR_FIXO'
      ? n.quantidade
      : n.passosPct.length;

/* ------------------------------------------------------------------ *
 * Balconista — o próprio bônus (fase 3)
 * ------------------------------------------------------------------ */

export interface MetaNoMes {
  readonly metaId: string;
  readonly nome: string;
  readonly descricao: string | null;
  readonly ordem: number;
  readonly indicador: string;
  readonly rotulo: string;
  readonly unidade: IndicadorDoCatalogo['unidade'];
  readonly casas: number;
  readonly direcao: Direcao;
  readonly comPremio: boolean;
  readonly nomesDosNiveis: readonly string[];
  readonly premiosDosNiveis: readonly (string | null)[] | null;
  readonly premioMaximo: string | null;
  readonly base: string | null;
  readonly valor: string | null;
  readonly amostra: number;
  readonly periodo: { readonly de: string; readonly ate: string } | null;
  readonly situacao: AvaliacaoDaMeta['situacao'] | 'NAO_COMECOU';
  readonly avaliacao: AvaliacaoDaMeta | null;
}
export interface BonusConsolidado {
  readonly pelasMetas: string | null;
  readonly teto: string | null;
  readonly projetado: string | null;
  readonly excedente: string | null;
  readonly potencialMaximo: string | null;
  readonly faltaParaMaximo: string | null;
  readonly proximoPotencial: string | null;
  readonly proximoGanho: string | null;
}
export interface MovimentoDoBonus {
  readonly id: string;
  readonly metaId: string;
  readonly meta: string;
  readonly tipo: 'SUBIU' | 'CAIU' | 'MAXIMO' | 'RISCO' | 'PERTO';
  readonly nivelAntes: number;
  readonly nivelDepois: number;
  readonly premioAntes: string | null;
  readonly premioDepois: string | null;
  /** Depois − antes, do servidor (negativo = o bônus projetado reduziu); `null` em "só números". */
  readonly diferenca: string | null;
  readonly valor: string | null;
  readonly em: string;
  readonly lido: boolean;
}
export interface MeuBonus {
  readonly competencia: string;
  readonly estado: 'PROJETADO' | 'CONFIRMADO' | 'PAGO';
  readonly fechamento: string;
  readonly pagamento: string;
  readonly mes: {
    readonly competencia: string;
    readonly metas: readonly MetaNoMes[];
    readonly bonus: BonusConsolidado;
  } | null;
  readonly movimentos: readonly MovimentoDoBonus[];
  readonly anteriores: readonly MesFechado[];
}
export const lerMeuBonus = (): Promise<Resultado<MeuBonus>> =>
  lerDaApi('/api/balconista/meu-bonus');
export const marcarLidos = (
  s: Sessao,
  ids: string[] | null,
): Promise<Escrita<{ marcados: number }>> =>
  escreverNaApi(s, 'POST', '/api/balconista/meu-bonus/lidos', { ids });

/* ------------------------------------------------------------------ *
 * Fase 5 — fechar o mês e pagar
 * ------------------------------------------------------------------ */

export interface FechamentoDoMes {
  readonly competencia: string;
  readonly estado: 'ABERTA' | 'FECHADA';
  readonly fechadaEm: string | null;
  readonly fechadaPor: string | null;
  readonly motivo: string | null;
  readonly pagamentoPrevisto: string | null;
  readonly podeFechar: boolean;
  readonly linhas: readonly {
    accountId: string;
    nome: string;
    pelasMetas: string;
    teto: string | null;
    excedente: string;
    confirmado: string;
    pagoEm: string | null;
    pagoValor: string | null;
    pagoPor: string | null;
  }[];
  readonly previa: readonly {
    accountId: string;
    nome: string;
    projetado: string;
    pelasMetas: string;
    excedente: string;
  }[];
}
export interface MesFechado {
  readonly competencia: string;
  readonly estado: 'CONFIRMADO' | 'PAGO';
  readonly confirmado: string | null;
  readonly pagoEm: string | null;
  readonly pagoValor: string | null;
  readonly pagamentoPrevisto: string;
  readonly metas: readonly { nome: string; nivel: number; premio: string | null }[];
}
export const lerFechamento = (competencia: string): Promise<Resultado<FechamentoDoMes>> =>
  lerDaApi(`/api/metas/fechamento?competencia=${competencia}`);
export const fecharMes = (
  s: Sessao,
  competencia: string,
  motivo: string,
): Promise<Escrita<{ balconistas: number; total: string }>> =>
  escreverNaApi(s, 'POST', '/api/metas/fechamento', { competencia, motivo });
export const registrarPagamento = (
  s: Sessao,
  d: { competencia: string; contas: string[]; pagoEm: string; motivo: string },
): Promise<Escrita<{ pagos: number; total: string }>> =>
  escreverNaApi(s, 'POST', '/api/metas/pagamento', d);
